import { useEffect, useRef, useState } from 'react'

const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'touchstart', 'scroll']

// Hook que dispara quando o utilizador esteve inativo por idleMs.
// Mostra aviso warningMs antes do logout para o utilizador cancelar.
//
//   timeoutMs: total até logout (default 30 min)
//   warningMs: quantos ms antes do logout mostrar o aviso (default 60 s)
//
// Devolve { showWarning, cancel, secondsLeft } — UI usa para renderizar o modal.
export function useIdleTimeout({
  enabled = true,
  timeoutMs = 30 * 60 * 1000,
  warningMs = 60 * 1000,
  onTimeout,
}) {
  const [showWarning, setShowWarning] = useState(false)
  const [secondsLeft, setSecondsLeft] = useState(Math.floor(warningMs / 1000))

  const lastActivityRef = useRef(Date.now())
  const warningTimerRef = useRef(null)
  const logoutTimerRef = useRef(null)
  const countdownTimerRef = useRef(null)

  const clearAllTimers = () => {
    if (warningTimerRef.current)   { clearTimeout(warningTimerRef.current);   warningTimerRef.current = null }
    if (logoutTimerRef.current)    { clearTimeout(logoutTimerRef.current);    logoutTimerRef.current = null }
    if (countdownTimerRef.current) { clearInterval(countdownTimerRef.current); countdownTimerRef.current = null }
  }

  const resetIdle = () => {
    lastActivityRef.current = Date.now()
    if (showWarning) return // não resetar enquanto o warning estiver visível — exige confirmação explícita
    clearAllTimers()
    warningTimerRef.current = setTimeout(() => {
      setShowWarning(true)
      setSecondsLeft(Math.floor(warningMs / 1000))
      countdownTimerRef.current = setInterval(() => {
        setSecondsLeft(s => (s > 0 ? s - 1 : 0))
      }, 1000)
      logoutTimerRef.current = setTimeout(() => {
        clearAllTimers()
        setShowWarning(false)
        onTimeout?.()
      }, warningMs)
    }, Math.max(0, timeoutMs - warningMs))
  }

  useEffect(() => {
    if (!enabled) { clearAllTimers(); setShowWarning(false); return }
    resetIdle()
    const handler = () => {
      if (!showWarning) resetIdle()
    }
    ACTIVITY_EVENTS.forEach(ev => window.addEventListener(ev, handler, { passive: true }))
    return () => {
      ACTIVITY_EVENTS.forEach(ev => window.removeEventListener(ev, handler))
      clearAllTimers()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, timeoutMs, warningMs, showWarning])

  const cancel = () => {
    clearAllTimers()
    setShowWarning(false)
    lastActivityRef.current = Date.now()
    resetIdle()
  }

  return { showWarning, cancel, secondsLeft }
}
